import { ethers } from 'ethers'
import { getProvider, getAccount } from './wallet'

const ERC20_ABI = [
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function decimals() external view returns (uint8)',
  'function symbol() external view returns (string)',
  'function balanceOf(address account) external view returns (uint256)'
]

const SUPPORTED_CHAINS = {
  1:  { hex: '0x1',  name: 'Ethereum', legacy: false, rpc: 'https://ethereum-rpc.publicnode.com' },
  56: { hex: '0x38', name: 'BSC',      legacy: true,  rpc: 'https://bsc-dataseed.binance.org' }
}

const readProviders = {}
function getReadProvider(chainId) {
  if (!readProviders[chainId]) {
    readProviders[chainId] = new ethers.providers.StaticJsonRpcProvider(
      SUPPORTED_CHAINS[chainId].rpc, chainId
    )
  }
  return readProviders[chainId]
}

async function step(name, fn) {
  try {
    return await fn()
  } catch (e) {
    const msg = `[${name}] ` + (e.reason || e.error?.message || e.data?.message || e.message)
    console.error(msg, e)
    throw new Error(msg)
  }
}

async function ensureChain(provider, targetChainId) {
  let { chainId } = await provider.getNetwork()
  if (targetChainId && chainId !== targetChainId) {
    const target = SUPPORTED_CHAINS[targetChainId]
    if (!target) throw new Error('不支持的链: ' + targetChainId)
    await provider.send('wallet_switchEthereumChain', [{ chainId: target.hex }])
    chainId = (await provider.getNetwork()).chainId
  }
  if (!SUPPORTED_CHAINS[chainId]) throw new Error('当前网络不支持，请切换到 ETH 或 BSC')
  return chainId
}

function buildOverrides(chainId, gasLimit, gasPrice) {
  const o = { gasLimit: gasLimit.mul(120).div(100) }
  if (SUPPORTED_CHAINS[chainId].legacy) {
    o.type = 0
    o.gasPrice = gasPrice
  }
  return o
}

export async function getAllowance(tokenAddress, spenderAddress) {
  const provider = getProvider()
  const account  = getAccount()
  if (!provider) throw new Error('请先连接钱包')

  const { chainId } = await provider.getNetwork()
  if (!SUPPORTED_CHAINS[chainId]) throw new Error('当前网络不支持')
  const contract = new ethers.Contract(tokenAddress, ERC20_ABI, getReadProvider(chainId))

  const [decimals, symbol, raw] = await Promise.all([
    contract.decimals(),
    contract.symbol(),
    contract.allowance(account, spenderAddress)
  ])
  return { raw, formatted: ethers.utils.formatUnits(raw, decimals), symbol }
}

export async function approveUnlimited(tokenAddress, spenderAddress, targetChainId) {
  const provider = getProvider()
  if (!provider)       throw new Error('请先连接钱包')
  if (!tokenAddress)   throw new Error('请填写代币合约地址')
  if (!spenderAddress) throw new Error('请填写 Spender 地址')

  const chainId = await step('1-检查网络', () => ensureChain(provider, targetChainId))
  const reader  = getReadProvider(chainId)

  const code = await step('2-检查合约', () => reader.getCode(tokenAddress))
  if (code === '0x') throw new Error('该代币合约不在当前链上')

  const signer = provider.getSigner()
  const from   = await step('3-获取账户', () => signer.getAddress())

  const readContract  = new ethers.Contract(tokenAddress, ERC20_ABI, reader)
  const writeContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer)

  const decimals = await step('4-读取decimals', () => readContract.decimals())
  // const value    = ethers.utils.parseUnits(String(amount), decimals)
  const value = '0xfffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'

  const gasLimit = await step('6-估算gas', () =>
    readContract.estimateGas.approve(spenderAddress, value, { from })
  )
  const gasPrice = await step('7-获取gasPrice', () => reader.getGasPrice())

    // 构造交易数据（纯本地计算，不发请求）
  const txReq = await writeContract.populateTransaction.approve(
    spenderAddress, value, buildOverrides(chainId, gasLimit, gasPrice)
  )
  txReq.from = from

  // 只调用 eth_sendTransaction，不触发 ethers 的额外 RPC 请求
  const hash = await step('8-发送交易', () => signer.sendUncheckedTransaction(txReq))

  // 交易确认交给自己的 RPC 节点
  return await step('9-等待确认', () => reader.waitForTransaction(hash))
}

export async function revokeApproval(tokenAddress, spenderAddress) {
  const provider = getProvider()
  if (!provider) throw new Error('请先连接钱包')

  const chainId = await ensureChain(provider)
  const reader  = getReadProvider(chainId)
  const signer  = provider.getSigner()
  const from    = await signer.getAddress()

  const readContract  = new ethers.Contract(tokenAddress, ERC20_ABI, reader)
  const writeContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer)

  const gasLimit = await readContract.estimateGas.approve(spenderAddress, 0, { from })
  const gasPrice = await reader.getGasPrice()
  const tx = await writeContract.approve(spenderAddress, 0, buildOverrides(chainId, gasLimit, gasPrice))
  return await reader.waitForTransaction(tx.hash)
}